import { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import CollapsibleGridCard from '../../components/CollapsibleGridCard';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../lib/api';
import { GRID_SESSION_KEYS } from '../../lib/gridSessionState';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const stageColors = {
    '01 - Lead': 'bg-light-primary',
    '02 - Identified': 'bg-light-dark',
    '03 - Committee': 'bg-light-secondary',
    '04 - Internal Revision': 'bg-light-danger',
    '05 - Committee Approval': 'bg-light-warning',
    '06 - Quote Sent': 'bg-light-primary',
    '07 - External Revision': 'bg-light-danger',
    '08 - Won': 'bg-light-success',
};
const columns = [
    { field: 'name', headerName: 'Name', flex: 1.5, minWidth: 200,
        renderCell: ({ row }) => <Link className="hive-grid-link-cell" to={`/opportunities/${row._id}`}>{row.name}</Link> },
    { field: 'projectNumber', headerName: 'Project #', flex: 1, minWidth: 110,
        renderCell: ({ value }) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography sx={{ color: '#592e7dff', fontWeight: 500, fontSize: '0.875rem' }}>{value || ''}</Typography>
        </Box> },
    { field: 'quoteNumber', headerName: 'Quote #', flex: 1, minWidth: 110,
        renderCell: ({ row }) => row.quoteId
            ? <Link className="hive-grid-link-cell" to={`/base-unique-quote/${row.quoteId}`}>{row.quoteNumber}</Link>
            : row.quoteNumber || '' },
    { field: 'customer', headerName: 'Customer', flex: 1, minWidth: 180,
        valueGetter: (value) => value?.organizationName || '',
        renderCell: ({ row }) => row.customer && <Link className="hive-grid-link-cell" to={`/customers/${row.customer._id}`}>{row.customer.organizationName}</Link> },
    { field: 'owner', headerName: 'Owner', flex: 1, minWidth: 150,
        valueGetter: (value) => [value?.firstName, value?.lastName].filter(Boolean).join(' ') || 'Unassigned' },
    { field: 'amount', headerName: 'Amount', type: 'number', flex: 1, minWidth: 130,
        valueFormatter: (value) => currency.format(value || 0),
        renderCell: ({ value }) => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
            <Typography sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '0.875rem' }}>{currency.format(value || 0)}</Typography>
        </Box> },
    { field: 'closeDate', headerName: 'Close Date', flex: 1, minWidth: 120,
        valueGetter: (value) => value ? value.split('T')[0] : '' },
    { field: 'salesStage', headerName: 'Stage', flex: 1, minWidth: 190,
        renderCell: ({ value }) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <span className={`badge ${stageColors[value] || 'bg-light-info'}`}
                style={{ minWidth: '75%', padding: '3px 8px', lineHeight: '1.4', fontSize: '14px' }}>{value}</span>
        </Box> },
];

const ContactOpportunities = ({ contactId }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);
    const { user } = useAuth();

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setRows([]);
        API.Contacts.FetchOpportunities(contactId)
            .then((data) => { if (active) setRows(data); })
            .catch((err) => { if (active) setError(err.response?.data?.message || 'Unable to load opportunities. Please try again.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [contactId, attempt]);

    return <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {error ? <Alert severity="error" action={<Button color="inherit" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}>{error}</Alert>
            : <CollapsibleGridCard
                title="Opportunities"
                rows={rows}
                columns={columns}
                getRowId={(row) => row._id}
                sessionStorageKey={`${GRID_SESSION_KEYS.contactOpportunities}:${contactId}`}
                sessionUserId={user?.userId || localStorage.getItem('id')}
                initialPageSize={25}
                pageSizeOptions={[25, 50, 100]}
                extraGridProps={{ loading, density: 'compact', initialState: { density: 'compact' }, disableRowSelectionOnClick: true, localeText: { noRowsLabel: 'No unarchived opportunities for this contact.' } }}
            />}
    </Box>;
};

export default ContactOpportunities;
